import React from "react";
import { Box, Typography, Paper, CardMedia } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { GalleryIcon } from "../ui/RecipeIcons";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { FetchedRecipe } from "../../types/recipe";

/**
 * Props for RecipeGallery component.
 * @property {FetchedRecipe} recipe - The recipe object containing gallery images.
 * @property {(image: string) => void} onImageClick - Callback for when an image is clicked.
 */
interface RecipeGalleryProps {
  recipe: FetchedRecipe;
  onImageClick: (image: string) => void;
}

/**
 * RecipeGallery Component
 *
 * Displays a gallery of images for the recipe.
 * Each image is clickable and triggers the provided callback.
 * Uses responsive flexbox layout and Material UI styling.
 *
 * Features:
 * - Shows gallery images if available
 * - Responsive wrapping layout
 * - Handles empty gallery gracefully
 *
 * @param {RecipeGalleryProps} props - Component props
 * @returns {JSX.Element|null} The rendered gallery section or null if no images
 */
const RecipeGallery: React.FC<RecipeGalleryProps> = ({
  recipe,
  onImageClick,
}) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  const galleryImages = Array.isArray(recipe.data.gallery)
    ? recipe.data.gallery.filter(
        (img) => img && typeof img.source === "string" && img.source.length > 0
      )
    : [];

  if (galleryImages.length === 0) {
    return (
      <Paper elevation={0} sx={styles.page.sectionCard}>
        <Box sx={styles.page.sectionHeader}>
          <GalleryIcon />
          <Typography variant="h5" sx={{ ...styles.page.sectionTitle, ml: 2 }}>
            Gallery Images
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No gallery images provided.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={styles.page.sectionCard}>
      {/* Section header with icon and title */}
      <Box sx={styles.page.sectionHeader}>
        <GalleryIcon />
        <Typography variant="h5" sx={{ ...styles.page.sectionTitle, ml: 2 }}>
          Gallery Images
        </Typography>
      </Box>
      {/* Gallery images grid */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 2 }}>
        {galleryImages.map((image, key) => (
          <Box
            sx={{
              flex: "1 1 200px",
              maxWidth: { xs: "100%", sm: "50%", md: "25%" },
            }}
            key={key}
          >
            <Box
              sx={styles.page.galleryImageContainer}
              onClick={() => onImageClick(image.source)}
            >
              <CardMedia
                component="img"
                image={getStaticAssetPath(image.source)}
                alt={`Gallery Image ${key + 1}`}
                sx={styles.page.galleryImage}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default RecipeGallery;
